<template>
  <div class="project-card__container">
    <div class="project-card__status" :style="{ 'background-color': projectColor }" />
    <div class="project-card">
      <div class="project-card_main tender-card_main pointer" @click="openTender">
        <div class="spacey-8 project-card__info">
          <router-link
            :to="{
              name: 'incoming-tender-summary',
              params: { incomingTenderId: tender.id },
            }"
            class="project-card__title grey-900 text-bs vertical-centering spacex-8 no-link"
          >
            {{ tenderInfo.name }}
            <InfoTooltip>
              <div class="spacey-12">
                <div v-if="tenderInfo.description">
                  <div class="text-bsb">{{ $t('Description') }}</div>
                  {{ tenderInfo.description }}
                </div>
                <div>
                  <span class="text-bsb">{{ $t('Organizer') }}</span> - {{ tenderInfo.team.name }}
                </div>
              </div>
            </InfoTooltip>
          </router-link>
          <div class="project-card__info-element text-sm grey-500 wrap-text">
            <span class="no-wrap spacex-4">
              <span>{{ $t('Offers deadline') }}</span>
              <span class="primary-600">{{ prettyDates(tenderInfo.offers_deadline_date) }}</span>
            </span>
            <span v-if="tender.currency" class="no-wrap">
              {{ currencyWithSymbol(tender.currency) }}
            </span>
            <span v-if="howMuchDaysLeft" class="warning-600 no-wrap">{{ howMuchDaysLeft }}</span>
            <span v-if="confirmParticipation" class="danger-600 no-wrap">
              {{ $t('Confirm participation') }}
            </span>
          </div>
        </div>
        <div class="project-card__members">
          <UserInfo :user="tender.contractor_contact_person_user" />
        </div>
        <div class="project-card__responsible">
          <MemberAvatars :members="tender.contractor_responsible_users" />
        </div>
        <div class="project-card__toggle">
          <button @click.stop="changeOffersDisplay">
            <Badge v-if="versions.length" :color="isActive ? 'light-blue' : 'grey'">
              {{ $tc('offer', versions.length) }}
              <ChevronDown class="icon-g-900" :class="{ 'turn-over': showOffers }" />
            </Badge>
          </button>
        </div>
        <div class="project-card__responsible">
          <BellIcon v-if="isActive" @click="openTender" />
        </div>
      </div>
      <keep-alive>
        <div v-if="showOffers" class="project-card_table spacey-12">
          <TenderCardOffers :offers="versions" :currency="tender.currency" />
          <Button
            v-if="isActive"
            size="small"
            left-icon="plus"
            type="borderless"
            @click="handleOfferCreation"
          >
            {{ isAtLeastOneDraft ? $t('Edit draft') : $t('Create new offer') }}
          </Button>
        </div>
      </keep-alive>
    </div>
  </div>
</template>

<script>
  import prettyDates from '@/utils/formatting/pretty-date';
  import UserInfo from '@/components/ui/UserInfo';
  import MemberAvatars from '@/components/tenders/MemberAvatars';
  import Badge from '@/components/ui/Badge';
  import ChevronDown from '@/assets/icons/chevron-down.svg';
  import BellIcon from '@/assets/icons/bell.svg';
  import TenderCardOffers from '@/containers/incoming-tender/TenderCardOffers';
  import InfoTooltip from '@/components/ui/InfoTooltip';
  import get from 'lodash.get';
  import { GREY_300 } from '@/utils/constants/colors';
  import { DEFAULT_COLOR } from '@/utils/constants/common';
  import PlusIcon from '@/assets/icons/plus.svg';
  import Button from '@/components/ui/Button';
  import CreateOfferModal from '@/containers/incoming-tender/CreateOfferModal';
  import { mapActions, mapGetters } from 'vuex';
  import daysLeft from '@/utils/date/days-left';

  export default {
    name: 'TenderCard',
    components: {
      UserInfo,
      MemberAvatars,
      Badge,
      ChevronDown,
      BellIcon,
      TenderCardOffers,
      InfoTooltip,
      Button,
    },
    props: {
      tender: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        showOffers: false,
        plusIcon: PlusIcon,
      };
    },
    computed: {
      ...mapGetters('global', ['currencyWithSymbol']),
      version() {
        return this.tender.current_version;
      },
      versions() {
        return this.tender.versions;
      },
      tenderInfo() {
        return this.tender.tender;
      },
      projectColor() {
        return this.isActive ? get(this.tender, 'tender.project.color', DEFAULT_COLOR) : GREY_300;
      },
      isActive() {
        return this.tender.status !== 'declined';
      },
      isAtLeastOneDraft() {
        return get(this.tender, 'versions', []).some((version) => version.status === 'draft');
      },
      confirmParticipation() {
        const status = this.tender.status;
        return status === 'read' || status === 'invited' || status === 'declined';
      },
      howMuchDaysLeft() {
        return daysLeft(this.tenderInfo.offers_deadline_date, [0, 6]);
      },
    },
    methods: {
      ...mapActions('modal', ['openModal']),
      prettyDates,
      daysLeft,
      changeOffersDisplay() {
        this.showOffers = !this.showOffers;
      },
      openTender() {
        this.$router.push({
          name: 'incoming-tender-summary',
          params: { incomingTenderId: this.tender.id },
        });
      },
      async handleOfferCreation() {
        await this.$router.push({
          name: 'incoming-tender-offers',
          params: { incomingTenderId: this.tender.id },
        });

        if (!this.isAtLeastOneDraft) {
          this.openModal({
            component: CreateOfferModal,
            context: {
              status: this.tenderInfo.status,
            },
          });
        }
      },
    },
  };
</script>
<style lang="scss">
  @import 'src/styles/components/project-card';
</style>
